import { startTransition, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ReportEvent } from '@/types/report';
import type { IdentityToken } from '@/types/identity';
import { encodeIdentityTokens, parseIdentityTokens } from '@/utils/identity';
import {
    DEFAULT_REPORT_CONFIDENCE_RANGE,
    DEFAULT_REPORT_EVENT_SORT_OPTION,
    DEFAULT_REPORT_GROUP_BY_MODE,
    REPORT_EVENT_SORT_OPTIONS,
    REPORT_GROUP_BY_OPTIONS,
    type ReportConfidenceRange,
    type ReportEventSortOption,
    type ReportGroupByMode,
} from '../constants';
import { filterReportEvents } from '../utils/filterReportEvents';
import { filterReportEventsByIdentity } from '../utils/filterReportEventsByIdentity';
import { sortReportEvents } from '../utils/sortReportEvents';

interface UseReportViewControlsResult {
    search: string;
    setSearch: (value: string) => void;
    sortOption: ReportEventSortOption;
    setSortOption: (option: ReportEventSortOption) => void;
    groupBy: ReportGroupByMode;
    setGroupBy: (mode: ReportGroupByMode) => void;
    confidence: ReportConfidenceRange;
    setConfidence: (range: ReportConfidenceRange) => void;
    resetConfidence: () => void;
    identityTokens: Set<IdentityToken>;
    toggleIdentity: (token: IdentityToken) => void;
    clearIdentitySelection: () => void;
    getVisibleEvents: (events: ReportEvent[]) => ReportEvent[];
}

export const URL_PARAM = {
    GROUP_BY: 'group_by',
    SORT_BY: 'sort_by',
    CONFIDENCE: 'confidence_range',
    SEARCH: 'search',
    IDENTITY: 'identity',
} as const;

function parseGroupBy(value: string | null): ReportGroupByMode {
    const match = REPORT_GROUP_BY_OPTIONS.find((option) => option.id === value);
    return match?.id ?? DEFAULT_REPORT_GROUP_BY_MODE;
}

function encodeSortOption(option: ReportEventSortOption): string {
    return option.field === 'manual'
        ? 'manual'
        : `${option.field}-${option.direction}`;
}

export function parseSortOption(value: string | null): ReportEventSortOption {
    const match = REPORT_EVENT_SORT_OPTIONS.find(
        (option) => option.id === value,
    );
    return match
        ? { field: match.field, direction: match.direction }
        : DEFAULT_REPORT_EVENT_SORT_OPTION;
}

function isDefaultSortOption(option: ReportEventSortOption): boolean {
    return (
        option.field === DEFAULT_REPORT_EVENT_SORT_OPTION.field &&
        option.direction === DEFAULT_REPORT_EVENT_SORT_OPTION.direction
    );
}

function encodeConfidenceRange(range: ReportConfidenceRange): string {
    return `${range.min}_${range.max}`;
}

function parseConfidenceRange(value: string | null): ReportConfidenceRange {
    if (!value) {
        return DEFAULT_REPORT_CONFIDENCE_RANGE;
    }
    const [minRaw, maxRaw] = value.split('_');
    const min = Number(minRaw);
    const max = Number(maxRaw);
    const isValid =
        Number.isFinite(min) &&
        Number.isFinite(max) &&
        min >= 0 &&
        max <= 100 &&
        min <= max;
    return isValid ? { min, max } : DEFAULT_REPORT_CONFIDENCE_RANGE;
}

function isDefaultConfidenceRange(range: ReportConfidenceRange): boolean {
    return (
        range.min === DEFAULT_REPORT_CONFIDENCE_RANGE.min &&
        range.max === DEFAULT_REPORT_CONFIDENCE_RANGE.max
    );
}

export function useReportViewControls(): UseReportViewControlsResult {
    const [searchParams, setSearchParams] = useSearchParams();

    const search = searchParams.get(URL_PARAM.SEARCH) ?? '';
    const groupBy = parseGroupBy(searchParams.get(URL_PARAM.GROUP_BY));
    const sortOption = useMemo(
        () => parseSortOption(searchParams.get(URL_PARAM.SORT_BY)),
        [searchParams],
    );
    const confidence = useMemo(
        () => parseConfidenceRange(searchParams.get(URL_PARAM.CONFIDENCE)),
        [searchParams],
    );

    // Writes a single report param while preserving any unrelated params, and
    // omits it entirely when at its default value to keep URLs clean. Uses
    // `replace` so action-bar tweaks don't pollute the history stack.
    const updateParam = useCallback(
        (key: string, value: string | null) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (value === null) {
                        next.delete(key);
                    } else {
                        next.set(key, value);
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const setSearch = useCallback(
        (value: string) => {
            updateParam(URL_PARAM.SEARCH, value.length > 0 ? value : null);
        },
        [updateParam],
    );

    const setSortOption = useCallback(
        (option: ReportEventSortOption) => {
            updateParam(
                URL_PARAM.SORT_BY,
                isDefaultSortOption(option) ? null : encodeSortOption(option),
            );
        },
        [updateParam],
    );

    const setGroupBy = useCallback(
        (mode: ReportGroupByMode) => {
            startTransition(() => {
                updateParam(
                    URL_PARAM.GROUP_BY,
                    mode === DEFAULT_REPORT_GROUP_BY_MODE ? null : mode,
                );
            });
        },
        [updateParam],
    );

    const setConfidence = useCallback(
        (range: ReportConfidenceRange) => {
            updateParam(
                URL_PARAM.CONFIDENCE,
                isDefaultConfidenceRange(range)
                    ? null
                    : encodeConfidenceRange(range),
            );
        },
        [updateParam],
    );

    const resetConfidence = useCallback(() => {
        updateParam(URL_PARAM.CONFIDENCE, null);
    }, [updateParam]);

    const identityTokens = useMemo(
        () => parseIdentityTokens(searchParams.get(URL_PARAM.IDENTITY)),
        [searchParams],
    );

    const toggleIdentity = useCallback(
        (token: IdentityToken) => {
            const next = new Set(identityTokens);
            if (next.has(token)) {
                next.delete(token);
            } else {
                next.add(token);
            }
            updateParam(URL_PARAM.IDENTITY, encodeIdentityTokens(next));
        },
        [identityTokens, updateParam],
    );

    const clearIdentitySelection = useCallback(() => {
        updateParam(URL_PARAM.IDENTITY, null);
    }, [updateParam]);

    const getVisibleEvents = useCallback(
        (events: ReportEvent[]) =>
            sortReportEvents(
                filterReportEventsByIdentity(
                    filterReportEvents(events, search),
                    identityTokens,
                ),
                sortOption,
            ),
        [search, sortOption, identityTokens],
    );

    return {
        search,
        setSearch,
        sortOption,
        setSortOption,
        groupBy,
        setGroupBy,
        confidence,
        setConfidence,
        resetConfidence,
        identityTokens,
        toggleIdentity,
        clearIdentitySelection,
        getVisibleEvents,
    };
}
